// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import prizeCurrencyFormat from '@/modules/casino/utils/prizeCurrencyFormat';
import { endpoints } from '../../api/endpoints';
import { RootState } from '../index';

type PlayerInfoResult = {
  data: {
    unsettled: any[];
    settled: [];
  };
  success: boolean;
};

type PlayerInfoError = {
  rejectValue: {
    error: string;
  };
};

export const fetchPlayerPrizes = createAsyncThunk<PlayerInfoResult, void, PlayerInfoError>(
  'retailPlayerPrizes/fetch',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState() as RootState;

      if (!state.authentication.access_token || state.authentication.auth_type !== 'user') {
        return rejectWithValue({
          error: 'Authentication token is missing or invalid',
        });
      }

      const [unsettled, settled]: any[] = await Promise.all([
        axios
          .get(endpoints.retailPlayerPrizes.unsettled, {
            headers: {
              Authorization: 'Bearer ' + state.authentication.access_token,
            },
          })
          .catch((e) => e),
        axios
          .post(
            endpoints.retailPlayerPrizes.settled,
            {
              offset: 0,
              limit: 20,
            },
            {
              headers: {
                Authorization: 'Bearer ' + state.authentication.access_token,
              },
            },
          )
          .catch((e) => e),
      ]);

      const data = {
        data: {
          unsettled: (unsettled.data || []).map((e: any) => {
            e.settled = false;
            return e;
          }),
          settled: (settled.data || []).map((e: any) => {
            e.settled = true;
            return e;
          }),
        },
        success: true,
      };

      return data;
    } catch (err: any) {
      const errResp = { error: err.toString() };
      return rejectWithValue(errResp);
    }
  },
);

export interface RetailPlayerPrizes {
  prizes: any[];
  unsettled: any[];
  settled: any[];
  loading: boolean;
  error: any;
}

export const retailPlayerPrizesSlice = createSlice({
  name: 'retailPlayerPrizes',
  initialState: <RetailPlayerPrizes>{
    prizes: [],
    unsettled: [],
    settled: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlayerPrizes.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          state.unsettled = action.payload.data.unsettled ?? [];
          state.settled = action.payload.data.settled ?? [];

          const prizes = [...action.payload.data.unsettled, ...action.payload.data.settled];

          prizes.sort((a, b) => {
            return (b?.meta?.spin_time ?? 0) - (a?.meta?.spin_time ?? 0);
          });

          prizes.forEach((prize) => {
            prize.currency_display_settings = prize?.reward_amount
              ? prizeCurrencyFormat(prize.amount_currency, prize.reward_amount)
              : prizeCurrencyFormat(prize.currency, prize.reward_value);
          });

          state.prizes = prizes;
        }
        console.log('fetchPlayerPrizes.fulfilled', action.payload, state);
      })
      .addCase(fetchPlayerPrizes.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPlayerPrizes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
        console.log('fetchPlayerInfo.rejected', action.payload);
      });
  },
});

export const { clearError } = retailPlayerPrizesSlice.actions;

export default retailPlayerPrizesSlice.reducer;
